import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Delete,
  OpenInNew,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { Video } from '../../types';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';

const PRO_WARM = '#ff8ba7';
const PRO_PINK_DEEP = '#e86a8a';

const UserVideos: React.FC = () => {
  const { pages: t, formatMessage } = useI18n();
  const [videos, setVideos] = useState<Video[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setIsLoading(true);
      const data = await api.getVideos();
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setIsLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      'video/*': ['.mp4', '.mov', '.avi'],
    },
    maxFiles: 1,
    noClick: false,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        try {
          setIsUploading(true);
          const formData = new FormData();
          formData.append('video', acceptedFiles[0]);

          await api.uploadVideo(formData);
          toast.success('Video uploaded successfully!');
          fetchVideos();
        } catch (error) {
          toast.error('Failed to upload video');
          console.error('Upload error:', error);
        } finally {
          setIsUploading(false);
        }
      }
    },
  });

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await api.deleteVideo(videoId);
      setVideos((prev) => prev.filter((video) => video._id !== videoId));
      toast.success('Video deleted successfully');
    } catch (error) {
      toast.error('Failed to delete video');
      console.error('Delete error:', error);
    }
  };

  return (
    <UserPageShell enter>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: { xs: 1.5, md: 2.5 },
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.35rem', sm: '1.75rem' },
            lineHeight: 1.2,
            color: PRO_PINK_DEEP,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          VIDEOS
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: { xs: 36, sm: 'auto' },
          }}
        >
          <IconButton
            aria-label={t.videos.dropIdle}
            onClick={open}
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              width: 36,
              height: 36,
              borderRadius: 2,
              color: PRO_PINK_DEEP,
              bgcolor: alpha(PRO_WARM, 0.18),
              border: '1px solid',
              borderColor: alpha(PRO_PINK_DEEP, 0.4),
              '&:hover': { bgcolor: alpha(PRO_WARM, 0.28) },
            }}
          >
            <CloudUpload fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        {...getRootProps()}
        sx={{
          display: { xs: 'none', sm: 'flex' },
          position: 'relative',
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.25, sm: 2 },
          px: { xs: 1.25, sm: 2.5 },
          py: { xs: 1, sm: 2.25 },
          mb: { xs: 1.5, md: 2.5 },
          borderRadius: 2.5,
          cursor: 'pointer',
          border: '1.5px solid',
          borderColor: isDragActive
            ? alpha(PRO_PINK_DEEP, 0.7)
            : alpha(PRO_PINK_DEEP, 0.45),
          background: isDragActive
            ? `linear-gradient(135deg, #fff 0%, ${alpha(PRO_WARM, 0.22)} 100%)`
            : `linear-gradient(135deg, #fff 0%, ${alpha(PRO_WARM, 0.14)} 100%)`,
          boxShadow: isDragActive
            ? `0 1px 2px rgba(15,23,42,0.08), 0 10px 28px ${alpha(PRO_PINK_DEEP, 0.22)}`
            : `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha(PRO_PINK_DEEP, 0.14)}`,
          transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: alpha(PRO_PINK_DEEP, 0.65),
            boxShadow: `0 2px 4px rgba(15,23,42,0.08), 0 12px 28px ${alpha(PRO_PINK_DEEP, 0.2)}`,
            transform: 'translateY(-1px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            backgroundImage:
              'radial-gradient(circle at 12% 20%, rgba(255,255,255,0.85) 0%, transparent 42%), radial-gradient(circle at 88% 78%, rgba(233,69,96,0.1) 0%, transparent 40%)',
            pointerEvents: 'none',
          },
        }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: 38, sm: 52 },
            height: { xs: 38, sm: 52 },
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(PRO_PINK_DEEP, 0.16),
            color: PRO_PINK_DEEP,
            boxShadow: `inset 0 0 0 1px ${alpha(PRO_PINK_DEEP, 0.28)}`,
            zIndex: 1,
          }}
        >
          {isUploading ? (
            <CircularProgress size={20} sx={{ color: PRO_PINK_DEEP }} />
          ) : (
            <CloudUpload sx={{ fontSize: { xs: 22, sm: 28 } }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0, position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '0.88rem', sm: '1rem' },
              color: PRO_PINK_DEEP,
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
              textAlign: 'center',
            }}
          >
            {isDragActive ? t.videos.dropActive : t.videos.dropIdle}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              display: { xs: 'none', sm: 'block' },
              mt: 0.15,
              fontSize: '0.8125rem',
              lineHeight: 1.3,
              textAlign: 'center',
              color: alpha(PRO_PINK_DEEP, 0.75),
            }}
          >
            {t.videos.maxOne}
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: PRO_PINK_DEEP }} />
        </Box>
      ) : (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          gap: { xs: 1.25, sm: 2.5 },
        }}
      >
        {videos.map((video, index) => (
          <Paper
            key={video._id}
            elevation={0}
            sx={{
              overflow: 'hidden',
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha(PRO_WARM, 0.28),
              boxShadow: `0 4px 14px ${alpha(PRO_WARM, 0.08)}`,
              background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(PRO_WARM, 0.08)} 100%)`,
              display: 'flex',
              flexDirection: 'column',
              ...userCardEnterSx(index),
            }}
          >
            <Box sx={{ position: 'relative', pt: '56.25%', bgcolor: alpha(PRO_WARM, 0.1) }}>
              <video
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                src={video.url}
                controls
              />
            </Box>
            <Box sx={{ p: { xs: 1.25, sm: 1.75 }, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: alpha(PRO_PINK_DEEP, 0.7), fontSize: { xs: '0.72rem', sm: '0.8125rem' } }}
              >
                {formatMessage(t.videos.created, {
                  date: new Date(video.createdAt).toLocaleDateString(),
                })}
              </Typography>
              <Typography
                fontWeight={700}
                sx={{
                  color: PRO_PINK_DEEP,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mt: 0.25,
                }}
              >
                {video.name}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 1, sm: 1.25 },
                pb: { xs: 1, sm: 1.25 },
              }}
            >
              <Button
                size="small"
                startIcon={<OpenInNew sx={{ fontSize: { xs: 14, sm: 18 } }} />}
                href={video.url}
                target="_blank"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: PRO_PINK_DEEP,
                  fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                  minWidth: 0,
                  px: { xs: 0.5, sm: 1 },
                  '& .MuiButton-startIcon': { mr: { xs: 0.35, sm: 0.75 } },
                }}
              >
                {t.common.view}
              </Button>
              <IconButton
                onClick={() => handleDeleteVideo(video._id)}
                size="small"
                sx={{
                  color: '#e53935',
                  '&:hover': { bgcolor: alpha('#e53935', 0.1) },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
      )}
    </UserPageShell>
  );
};

export default UserVideos;
